import React from 'react';

import type {
  FingeringGroup,
  NoteheadData,
} from '../../hooks/usePdfStateQueue';
import { accidentalBboxColor } from '../../utils/accidentalBboxColor';

import {
  computeOverlayExpansion,
  fingeringStackGeometry,
} from './computeOverlayExpansion';
import type { FingeringSizeConfig } from './fingeringSizes';

// Diatonic pitch letters, indexed by `pitchStep` (0..6). Matches the
// `PITCH_STEP_LETTERS` table in `AI_core/omr_pipeline/richnotes.py`.
const PITCH_LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;

interface FingeringOverlayProps {
  fingeringGroups: FingeringGroup[];
  noteheadDataList?: NoteheadData[];
  imageWidth: number;
  imageHeight: number;
  displayWidth: number;
  displayHeight: number;
  imageIndex: number;
  sizeConfig: FingeringSizeConfig;
  showAccidental: boolean;
  showPitch: boolean;
  showFingerings?: boolean;
  showAltFingerings?: boolean;
}

/**
 * Per-system overlay rendered on top of a single score PNG. Mounted
 * imperatively via `createRoot` from `PdfProjectPage`'s main rebuild
 * effect (see comments there for the layout / sizing contract).
 *
 * The component owns three visual layers:
 *   1. Accidental tints (HTML `<div>`s with `mix-blend-mode: lighten`,
 *      rendered BEFORE the SVG so they sit behind the fingering pills
 *      but still cascade their blend mode up to the per-system
 *      `container` div's isolated stacking context — see the inline
 *      comments for why this can't be SVG `<rect>`s).
 *   2. Fingering pills + digits (SVG, on top of the blend layer).
 *   3. Pitch letters (SVG, white fill + thin black rim so they stay
 *      legible on both black and hollow noteheads).
 *
 * Both the accidental and pitch layers are toggled via the `display`
 * style by a sibling toggle effect in `PdfProjectPage` rather than
 * via React props alone — that avoids forcing a full overlay rebuild
 * (which would `innerHTML = ''` the PNG container and shift the
 * spacing between systems).
 */
export const FingeringOverlay: React.FC<FingeringOverlayProps> = ({
  fingeringGroups,
  noteheadDataList,
  imageWidth,
  imageHeight,
  displayWidth,
  displayHeight,
  imageIndex,
  sizeConfig,
  showAccidental,
  showPitch,
  showFingerings = true,
  showAltFingerings = true,
}) => {
  const { fontSize, groupWidth, borderRadius } = sizeConfig;

  // Calculate scale factors for coordinate mapping
  const scaleX = displayWidth / imageWidth;
  const scaleY = displayHeight / imageHeight;

  // Pull the per-system expansion from the shared helper. Must match
  // exactly what the outer effect uses for `container.paddingTop` /
  // `paddingBottom`; see the helper's comment for why.
  const { maxExpansionUp, maxExpansionDown } = computeOverlayExpansion(
    fingeringGroups,
    sizeConfig,
    { scaleY, displayHeight, showFingerings, showAltFingerings }
  );
  const altDisplay = showFingerings && showAltFingerings ? 'block' : 'none';

  const coordinateOffsetY = maxExpansionUp;
  const totalHeight = displayHeight + maxExpansionUp + maxExpansionDown;

  return (
    <>
      {/* Accidental overlay — rendered as HTML <div>s, NOT SVG rects,
          and as a SIBLING to the <svg> below. This placement is
          essential for `mix-blend-mode: lighten` to actually tint
          the engraved notehead: SVG paints its children into an
          isolated offscreen group buffer, so `mix-blend-mode` on an
          SVG `<rect>` only blends with other SVG content (which is
          transparent around the notehead) — the result is a solid
          colored bbox instead of a tint. HTML elements participate
          in the page-level stacking context, so the `lighten` blend
          here samples the underlying <img> (via the per-system
          `container` div's `isolation: isolate`) and the alter color
          shows up only where the notehead is darker than the color
          in each channel.

          Visibility is flipped via `display: block/none` on this
          wrapper by the sibling toggle useEffect — same in-place
          mutation pattern used for the alt-fingering group, so
          toggling doesn't trigger the main rebuild effect (which
          would `innerHTML = ''` the PNG container and shift the
          spacing between systems). */}
      {noteheadDataList && noteheadDataList.length > 0 && (
        <div
          data-overlay-type="accidental"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            display: showAccidental ? 'block' : 'none',
            // INTENTIONALLY no z-index here. Adding z-index would
            // turn this wrapper into its own stacking context, which
            // Chrome's compositor refuses to pass `mix-blend-mode`
            // through reliably even when the spec says it should.
            // Without z-index, this wrapper is a plain positioned
            // div with no stacking context, so the rect children's
            // `lighten` blend cascades up to the nearest isolated
            // ancestor — the per-system `container` div — and
            // correctly samples the <img> as backdrop. The wrapper
            // still paints under the <svg> sibling because the SVG
            // does have z-index 10 and they're both positioned.
          }}
        >
          {noteheadDataList
            .filter((nd) => nd.alter !== 0)
            .map((nd) => {
              const x = nd.x1 * scaleX;
              const y = nd.y1 * scaleY + coordinateOffsetY;
              const w = (nd.x2 - nd.x1) * scaleX;
              const h = (nd.y2 - nd.y1) * scaleY;
              // Color encodes alter direction *and* magnitude — see
              // the `accidentalBboxColor` helper for the palette
              // + readability rationale.
              const fillColor = accidentalBboxColor(nd.alter);
              return (
                <div
                  key={`acc-${nd.noteheadId}`}
                  style={{
                    position: 'absolute',
                    left: `${x}px`,
                    top: `${y}px`,
                    width: `${w}px`,
                    height: `${h}px`,
                    backgroundColor: fillColor,
                    // Per-channel `max(backdrop, source)`. Wherever
                    // the engraved notehead is darker than the
                    // alter color, the alter color shows through;
                    // wherever the paper is lighter (mostly white),
                    // the paper stays. Result: the notehead itself
                    // picks up the tint, the surrounding paper
                    // remains clean — no rectangular border needed.
                    mixBlendMode: 'lighten',
                    pointerEvents: 'none',
                  }}
                  data-notehead-id={nd.noteheadId}
                  data-alter={nd.alter}
                />
              );
            })}
        </div>
      )}

      <svg
        width="100%" // Fill parent container completely
        height="100%" // Fill parent container completely
        viewBox={`0 0 ${displayWidth} ${totalHeight}`}
        preserveAspectRatio="none" // Fill container without maintaining aspect ratio
        style={{
          display: 'block', // Remove default inline spacing
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'auto', // Allow interaction with child elements
          zIndex: 10,
        }}
      >
        {fingeringGroups.map((fingeringGroup, groupIdx) => {
          const { upward } = fingeringGroup.coordinate;
          // Same geometry the canvas padding is sized from.
          const stack = fingeringStackGeometry(
            fingeringGroup,
            sizeConfig,
            scaleX,
            scaleY,
            coordinateOffsetY
          );
          const scaledX = stack.x;
          const pillX = scaledX - groupWidth / 2;

          return (
            <g key={groupIdx} data-direction={upward ? 'upward' : 'downward'}>
              <g
                data-overlay-type="fingerings"
                style={{ display: showFingerings ? 'block' : 'none' }}
              >
                {/* Main group pill — drawn first so digits sit on top of it. */}
                {stack.mainPill && (
                  <rect
                    x={pillX}
                    y={stack.mainPill.y}
                    width={groupWidth}
                    height={stack.mainPill.height}
                    rx={borderRadius}
                    ry={borderRadius}
                    fill="rgb(238, 241, 254)"
                    fillOpacity={0.8}
                    style={{ pointerEvents: 'none' }}
                    data-fingering-type="main"
                  />
                )}

                {/* Alt group pill — slightly more transparent so the
                main pill reads as the primary suggestion. */}
                {stack.altPill && (
                  <rect
                    x={pillX}
                    y={stack.altPill.y}
                    width={groupWidth}
                    height={stack.altPill.height}
                    rx={borderRadius}
                    ry={borderRadius}
                    fill="rgb(238, 241, 254)"
                    fillOpacity={0.35}
                    style={{ pointerEvents: 'none', display: altDisplay }}
                    data-fingering-type="alt"
                  />
                )}

                {/* Draw main fingerings */}
                {stack.mains.map(({ value: mainFingering, y: mainY }, i) => {
                  return (
                    <g key={`main-${i}`} data-fingering-type="main">
                      {/* Main fingering number */}
                      <text
                        x={scaledX}
                        y={mainY}
                        textAnchor="middle"
                        fontSize={fontSize}
                        fontWeight="bold"
                        fill="#5c6bff" // Matches mobile main fingering color
                        dominantBaseline="central"
                        style={{
                          cursor: 'pointer',
                          pointerEvents: 'all',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTouchCallout: 'none',
                          touchAction: 'none',
                        }}
                        data-fingering-type="main"
                        data-image-index={imageIndex}
                        data-group-index={groupIdx}
                        data-fingering-index={i}
                      >
                        {mainFingering}
                      </text>
                    </g>
                  );
                })}
              </g>

              {/* Draw alt fingerings */}
              {stack.alts.map(({ value: altFingering, y: altY }, i) => {
                return (
                  <g key={`alt-${i}`} data-fingering-type="alt">
                    {/* Alt fingering number */}
                    <text
                      x={scaledX}
                      y={altY}
                      textAnchor="middle"
                      fontSize={fontSize}
                      fontWeight="bold"
                      fill="#bfc6ff" // Matches mobile alt fingering color
                      dominantBaseline="central"
                      style={{
                        cursor: 'pointer',
                        pointerEvents: 'all',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        WebkitTouchCallout: 'none',
                        touchAction: 'none',
                        display: altDisplay,
                      }}
                      data-fingering-type="alt"
                      data-image-index={imageIndex}
                      data-group-index={groupIdx}
                      data-fingering-index={i}
                    >
                      {altFingering}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Pitch-overlay group: diatonic letter (C..B) drawn centered on
          every notehead bbox. Same gating as the accidental group; the
          letter mapping mirrors AI_core's `PITCH_STEP_LETTERS` order
          (C=0, D=1, E=2, F=3, G=4, A=5, B=6).

          Font size scales with the notehead bbox height (in display
          pixels) so the letter always fits comfortably inside the
          notehead regardless of the page zoom or `fingeringSize`. */}
        {/* Font size is derived ONCE per system from the median notehead
          bbox height (not per-note) so every letter in this system
          renders at the same visual size. Per-note sizing caused
          inconsistent vertical offsets across rows: small bbox-height
          detection noise produced slightly different font sizes per
          letter, and the SVG baseline math (see below) shifts the
          apparent center by a fraction that scales with font size —
          so rows with bigger noteheads ended up visibly more off than
          rows with smaller ones.

          Vertical centering uses `dy=".35em"` against the default
          alphabetic baseline rather than `dominantBaseline="middle"`
          / `"central"`. Those modes align on the font's x-height or
          EM-box midpoints, both of which sit ABOVE the visual center
          of capital glyphs (no descenders to balance). `dy=".35em"`
          empirically lands uppercase letters with their cap-height
          midpoint on the target y across Chrome/Firefox/Safari. */}
        {noteheadDataList &&
          noteheadDataList.length > 0 &&
          (() => {
            const sortedHeights = noteheadDataList
              .map((nd) => (nd.y2 - nd.y1) * scaleY)
              .filter((h) => h > 0)
              .sort((a, b) => a - b);
            const medianHeight =
              sortedHeights.length > 0
                ? sortedHeights[Math.floor(sortedHeights.length / 2)]
                : fontSize;
            const pitchFontSize = Math.max(
              6,
              Math.min(fontSize, medianHeight * 1)
            );
            // Thin black rim around each letter so the glyph stays
            // legible against the accidental bbox tints and against
            // the staff. White fill + black rim is the same trick
            // used on highway signage / map labels.
            //
            // `paintOrder="stroke"` reorders the default `fill stroke`
            // sequence so the stroke renders *behind* the fill. Without
            // it, a 1px stroke eats 0.5px on each side of the glyph
            // outline, thinning the letter; with stroke-first, only the
            // outer half is visible and the letter reads at its full
            // weight. `strokeLinejoin="round"` rounds the corners on
            // serifed or angular letter terminals.
            //
            // Stroke width scales with font size (8%) so the rim stays
            // visually proportional whether the system is small or large.
            const pitchRimWidth = pitchFontSize * 0.08;
            return (
              <g
                data-overlay-type="pitch"
                style={{ display: showPitch ? 'block' : 'none' }}
              >
                {noteheadDataList.map((nd) => {
                  const letter =
                    nd.pitchStep >= 0 && nd.pitchStep < PITCH_LETTERS.length
                      ? PITCH_LETTERS[nd.pitchStep]
                      : '?';
                  const cx = ((nd.x1 + nd.x2) / 2) * scaleX;
                  const cy = ((nd.y1 + nd.y2) / 2) * scaleY + coordinateOffsetY;
                  return (
                    <text
                      key={`pitch-${nd.noteheadId}`}
                      x={cx}
                      y={cy}
                      dy=".35em"
                      textAnchor="middle"
                      fontSize={pitchFontSize}
                      fontWeight="bold"
                      fill="#fff"
                      stroke="#000"
                      strokeWidth={pitchRimWidth}
                      paintOrder="stroke"
                      strokeLinejoin="round"
                      style={{
                        pointerEvents: 'none',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                      }}
                      data-notehead-id={nd.noteheadId}
                      data-pitch-step={nd.pitchStep}
                    >
                      {letter}
                    </text>
                  );
                })}
              </g>
            );
          })()}
      </svg>
    </>
  );
};
